import React, { forwardRef } from 'react';
import './Input.css';

/**
 * Modern Input Component
 * 
 * @param {string} label - Input label
 * @param {string} error - Error message
 * @param {string} helperText - Helper text below input
 * @param {React.ReactNode} leftIcon - Icon inside input on left
 * @param {React.ReactNode} rightIcon - Icon inside input on right
 * @param {boolean} fullWidth - Full width container
 */
const Input = forwardRef(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  id,
  type = 'text',
  disabled,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const containerClass = [
    'ui-input-container',
    fullWidth ? 'ui-input-container--block' : '',
    disabled ? 'ui-input-container--disabled' : '',
    error ? 'ui-input-container--error' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClass}>
      {label && <label htmlFor={inputId} className="ui-input-label">{label}</label>}
      
      <div className="ui-input-wrapper">
        {leftIcon && <span className="ui-input-icon ui-input-icon--left">{leftIcon}</span>}
        
        <input
          ref={ref}
          id={inputId}
          type={type}
          className={`ui-input ${leftIcon ? 'ui-input--has-left-icon' : ''} ${rightIcon ? 'ui-input--has-right-icon' : ''}`}
          disabled={disabled}
          {...props}
        />
        
        {rightIcon && <span className="ui-input-icon ui-input-icon--right">{rightIcon}</span>}
      </div>

      {(error || helperText) && (
        <span className={`ui-input-message ${error ? 'ui-input-message--error' : ''}`}>
          {error || helperText}
        </span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
